import {InvalidKeyError} from 'metaserver/static/js/encrypted_folder/crypto/errors';
import type {PublicKey} from 'metaserver/static/js/encrypted_folder/crypto/hpke/types';
import {
  bigIntCompare,
  CompareResult,
} from 'metaserver/static/js/encrypted_folder/crypto/hpke/utils';
import {
  bytesToHexString,
  hexStringToBytes,
} from 'metaserver/static/js/encrypted_folder/utils/bytes';

// Validation functions for ECC public keys, implemented for P-256.
// https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-56Ar3.pdf, section 5.6.2.3.4

// https://www.rfc-editor.org/rfc/rfc6090.html
export const P256_FIELD_PRIME = 'ffffffff00000001000000000000000000000000ffffffffffffffffffffffff';

export async function validatePublicKey(key: PublicKey): Promise<void> {
  // NOTE: WebCrypto rejects the identity element as an invalid public key, but that doesn't seem to be documented.
  // We therefore introduce helper methods that we *can* test.

  const rawKey = await crypto.subtle.exportKey('raw', key).catch(() => {
    throw new InvalidKeyError();
  });

  // step 1
  await assertNotIdentityElement(new Uint8Array(rawKey));
  // step 2
  await assertInsideField(new Uint8Array(rawKey));
  // step 3
  await assertOnCurve(key);
}

export async function assertNotIdentityElement(key: Uint8Array): Promise<void> {
  // https://w3c.github.io/webcrypto/#ecdh-operations specifies that "raw" exports in the format specified by
  // https://www.secg.org/SEC1-Ver-1.0.pdf, (uncompressed), section 2.3.3. SEC1 in turn specifies that the
  // identity element is encoded as 0x00.
  if (bytesToHexString(key) === '00') {
    throw new InvalidKeyError();
  }
}

export async function assertInsideField(key: Uint8Array): Promise<void> {
  // Both x and y must be within [0, p-1], where p is the prime of the curve.

  // https://w3c.github.io/webcrypto/#ecdh-operations specifies that "raw" exports in the format specified by
  // https://www.secg.org/SEC1-Ver-1.0.pdf, (uncompressed).
  const prefixStrippedKey = key.slice(1);
  const x = prefixStrippedKey.slice(0, prefixStrippedKey.length / 2);
  const y = prefixStrippedKey.slice(prefixStrippedKey.length / 2);
  const p = hexStringToBytes(P256_FIELD_PRIME);

  if (
    bigIntCompare(x, hexStringToBytes('00')) === CompareResult.LeftSmallerThanRight ||
    bigIntCompare(x, p) === CompareResult.LeftBiggerThanRight ||
    bigIntCompare(x, p) === CompareResult.Equal ||
    bigIntCompare(y, hexStringToBytes('00')) === CompareResult.LeftSmallerThanRight ||
    bigIntCompare(y, p) === CompareResult.LeftBiggerThanRight ||
    bigIntCompare(y, p) === CompareResult.Equal
  ) {
    throw new InvalidKeyError();
  }
}

export async function assertOnCurve(key: PublicKey): Promise<void> {
  // https://w3c.github.io/webcrypto/#ecdh-operations prescribes a check that the public key is a valid point
  // on the curve (§24.4, "Import Key", "spki", step 17). Therefore, we can efficiently test for this by
  // exporting and re-importing the key.
  const spkiKey = await crypto.subtle.exportKey('spki', key).catch(() => {
    throw new InvalidKeyError();
  });
  await crypto.subtle
    .importKey('spki', spkiKey, {name: 'ECDH', namedCurve: 'P-256'}, false, [])
    .catch(() => {
      throw new InvalidKeyError();
    });
}
